import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMyPrivileges } from '../hooks/useMyPrivileges';

// Navigation items for admin/manager roles
const adminNavigationItems = [
  {
    text: 'Dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    path: '/dashboard'
  },
  {
    text: 'All Jobs',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    path: '/dashboard/schedule'
  },
  {
    text: 'My Jobs',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    path: '/dashboard/my-schedule'
  },
  {
    text: 'Job Assignments',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    path: '/dashboard/assignments'
  },
  {
    text: 'Properties',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    path: '/dashboard/properties'
  },
  {
    text: 'Reservations',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    path: '/dashboard/reservations'
  },
  {
    text: 'Work Orders',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      </svg>
    ),
    path: '/dashboard/work-orders'
  },
  {
    text: 'Lost and Found',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    path: '/dashboard/lost-found'
  }
];

// Guest app items for admin/manager roles
const guestAppItems = [
  {
    text: 'App Invites',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
      </svg>
    ),
    path: '/dashboard/invites'
  },
  {
    text: 'Orders',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    path: '/dashboard/orders'
  },
  {
    text: 'Products',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    ),
    path: '/dashboard/products'
  },
  {
    text: 'Where to Go',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    path: '/dashboard/where-to-go'
  }
];

const helpItems = [
  {
    text: 'Documentation',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    path: '/dashboard/help/documentation'
  },
  {
    text: 'Tutorials',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    path: '/dashboard/help/tutorials'
  },
  {
    text: 'Developers',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    path: '/dashboard/help/developers',
    adminOnly: true
  }
];

// Navigation items for housekeeper role
const housekeeperNavigationItems = [
  {
    text: 'Dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    path: '/dashboard'
  },
  {
    text: 'My Jobs',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    path: '/dashboard/my-schedule'
  },
  {
    text: 'Completed Jobs',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    path: '/dashboard/completed-jobs'
  },
  // Full Schedule item removed for HOUSEKEEPER role
];

// Team items
const teamItems = [
  {
    text: 'Time Clock',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    path: '/dashboard/time-clock'
  },
  {
    text: 'Team Members',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    path: '/dashboard/team-members'
  },
  {
    text: 'Team Availability',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    path: '/dashboard/team-availability'
  },
  {
    text: 'Map Locations',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    path: '/dashboard/gps-tracking'
  }
];

// Reporting items
const reportingItems = [
  {
    text: 'Service & Expense Reports',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    path: '/dashboard/reports',
    roles: ['ADMIN', 'GUEST_APP_MANAGER']
  },
  {
    text: 'Hourly Payroll',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    path: '/dashboard/hourly-payroll',
    adminOnly: true
  },
  {
    text: 'Work Order Reports',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    path: '/dashboard/reports/work-orders',
    roles: ['ADMIN', 'GUEST_APP_MANAGER', 'PROPERTY_MANAGER']
  },
  {
    text: 'Property Performance',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 13.5l4.5-4.5 3 3L16.5 6 21 10.5M5 20h14" />
      </svg>
    ),
    path: '/dashboard/reports/property-performance',
    roles: ['ADMIN', 'PROPERTY_MANAGER']
  }
];

const CollapsibleNavSection = ({ id, title, children, className = '', defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`mt-8 lg:mt-6 pt-6 lg:pt-4 border-t border-sky-900 ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={`${id}-navigation-items`}
        className="w-full px-7 mb-4 lg:mb-2 flex items-center justify-between text-sm lg:text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 rounded-md"
      >
        <span>{title}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        id={`${id}-navigation-items`}
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 space-y-1">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default function Navigation({ toggleMobileMenu }) {
  const { user } = useAuth();
  const { hasPrivilege } = useMyPrivileges();
  const userRole = user?.role || '';
  
  // Define restricted roles - roles that see limited main navigation.
  const restrictedRoles = ['INSPECTOR', 'MAINTENANCE', 'CALL_CENTER'];
  const isRestrictedRole = restrictedRoles.includes(userRole);
  const isHousekeeper = userRole === 'HOUSEKEEPER';
  const isHousekeeperManager = userRole === 'HOUSEKEEPER_MANAGER';
  const isPropertyManager = userRole === 'PROPERTY_MANAGER';
  const isGuestAppManager = userRole === 'GUEST_APP_MANAGER';
  const isAdmin = userRole === 'ADMIN';
  
  // Define access permissions per role
  // For Inspector and Maintenance roles, hide certain sections
  // Allow every authenticated role except HOUSEKEEPER to access Team Members
  // GUEST_APP_MANAGER gets same permissions as ADMIN
  const canAccessAssignments = !isRestrictedRole && (!isHousekeeper || isHousekeeperManager);
  const canAccessTeamMembers = Boolean(userRole) && !isHousekeeper;
  const canAccessTeamAvailability = hasPrivilege('TEAM_AVAILABILITY');
  const canAccessTimeClock = isAdmin;
  const canAccessPayroll = !isRestrictedRole && !isHousekeeper && !isHousekeeperManager && (!isPropertyManager || isGuestAppManager || isAdmin);
  const canAccessReports = canAccessPayroll || isPropertyManager;
  const canAccessGPSTracking = ['ADMIN', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER', 'CALL_CENTER'].includes(userRole);
  const canAccessGuestApp = !isRestrictedRole && !isHousekeeper && !isHousekeeperManager && (!isPropertyManager || isGuestAppManager || isAdmin);
  const canAccessSettings = !isRestrictedRole && !isHousekeeper && !isHousekeeperManager && (!isPropertyManager || isGuestAppManager || isAdmin);
  const canAccessWorkOrders = (!isHousekeeper || isHousekeeperManager) && userRole !== 'INSPECTOR';
  const canAccessFullSchedule = userRole !== 'INSPECTOR';
  const canAccessProperties = userRole !== 'INSPECTOR';
  const canAccessReservations = userRole !== 'INSPECTOR' && userRole !== 'MAINTENANCE';
  const canAccessLostAndFound = userRole !== 'INSPECTOR';

  
  // Get the work orders item (default path for most roles)
  const workOrdersItem = adminNavigationItems.find(item => item.path === '/dashboard/work-orders');
  
  // CALL_CENTER lands on the all-status view by default; everyone else uses the standard work orders page
  const workOrdersItemForRole = userRole === 'CALL_CENTER' && workOrdersItem
    ? { ...workOrdersItem, path: '/dashboard/work-orders-all-status' }
    : workOrdersItem;
  
  // Get the lost and found item
  const lostAndFoundItem = adminNavigationItems.find(item => item.path === '/dashboard/lost-found');
  
  // Get the properties item
  const propertiesItem = adminNavigationItems.find(item => item.path === '/dashboard/properties');
  
  // Get the assignments item
  const assignmentsItem = adminNavigationItems.find(item => item.path === '/dashboard/assignments');
  
  // Get the full schedule item
  const fullScheduleItem = adminNavigationItems.find(item => item.path === '/dashboard/schedule');
  
  // Get the reservations item
  const reservationsItem = adminNavigationItems.find(item => item.path === '/dashboard/reservations');
  
  // Filter navigation items based on role
  const filteredNavigationItems = adminNavigationItems.filter(item => {
    if (item.path === '/dashboard/schedule' && !canAccessFullSchedule) return false;
    if (item.path === '/dashboard/assignments' && !canAccessAssignments) return false;
    if (item.path === '/dashboard/reservations' && (userRole === 'MAINTENANCE' || !canAccessReservations)) return false;
    if (item.path === '/dashboard/properties' && !canAccessProperties) return false;
    if (item.path === '/dashboard/work-orders' && !canAccessWorkOrders) return false;
    if (item.path === '/dashboard/lost-found' && !canAccessLostAndFound) return false;
    return true;
  });
  
  // Create completed jobs item
  const completedJobsItem = {
    text: 'Completed Jobs',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    path: '/dashboard/completed-jobs'
  };

  // Update navigation items for different roles
  let navigationItems;
  if (isHousekeeper) {
    navigationItems = housekeeperNavigationItems;
  } else if (isHousekeeperManager) {
    // HOUSEKEEPER_MANAGER gets base items + full schedule + assignments + properties + work orders + lost and found
    navigationItems = [
      ...housekeeperNavigationItems, 
      fullScheduleItem,
      assignmentsItem,
      propertiesItem,
      workOrdersItemForRole,
      lostAndFoundItem
    ].filter(Boolean);
  } else if (userRole === 'PROPERTY_MANAGER') {
    // PROPERTY_MANAGER gets limited navigation but includes assignments and lost and found
    navigationItems = [
      adminNavigationItems.find(item => item.path === '/dashboard'),
      adminNavigationItems.find(item => item.path === '/dashboard/schedule'),
      adminNavigationItems.find(item => item.path === '/dashboard/my-schedule'),
      assignmentsItem,
      propertiesItem,
      reservationsItem,
      workOrdersItemForRole,
      lostAndFoundItem
    ].filter(Boolean);
  } else if (userRole === 'MAINTENANCE') {
    // MAINTENANCE gets limited navigation - no reservations but includes properties
    navigationItems = [
      adminNavigationItems.find(item => item.path === '/dashboard'),
      adminNavigationItems.find(item => item.path === '/dashboard/my-schedule'),
      propertiesItem,
      workOrdersItemForRole,
      lostAndFoundItem
    ].filter(Boolean);
  } else if (userRole === 'CALL_CENTER') {
    // CALL_CENTER sees Dashboard, All Jobs (schedule), Properties, Reservations, Work Orders, and Lost and Found
    // CALL_CENTER lands on the all-status work orders view by default
    navigationItems = [
      adminNavigationItems.find(item => item.path === '/dashboard'),
      fullScheduleItem,
      propertiesItem,
      reservationsItem,
      workOrdersItemForRole,
      lostAndFoundItem
    ].filter(Boolean);
  } else if (userRole === 'INSPECTOR') {
    // INSPECTOR gets limited navigation - no properties, lost and found, reservations, or work orders
    // But includes completed jobs
    navigationItems = [
      adminNavigationItems.find(item => item.path === '/dashboard'),
      adminNavigationItems.find(item => item.path === '/dashboard/my-schedule'),
      completedJobsItem
    ].filter(Boolean);
  } else if (isGuestAppManager || isAdmin) {
    // GUEST_APP_MANAGER and ADMIN get full access to all navigation items
    navigationItems = adminNavigationItems;
  } else {
    navigationItems = filteredNavigationItems;
  }

  // Handle link click to close mobile menu
  const handleLinkClick = (e) => {
    // Don't prevent default - let React Router handle navigation
    if (window.innerWidth < 1024) { // lg breakpoint in Tailwind
      setTimeout(() => toggleMobileMenu(), 100); // Small delay to allow navigation to start
    }
  };

  return (
    <nav className="py-2 w-full">
      {/* Navigation Items */}
      <div className="space-y-1 px-4 lg:space-y-1">
        {navigationItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={handleLinkClick}
            className="block px-4 py-4 lg:px-3 lg:py-2 text-base lg:text-sm rounded-xl lg:rounded-lg text-gray-300 hover:bg-sky-900 active:bg-sky-800 hover:text-white transition-all duration-200 flex items-center gap-4 lg:gap-3 font-medium lg:font-normal border border-transparent hover:border-sky-700 lg:border-0 shadow-sm lg:shadow-none"
          >
            <div className="w-6 h-6 lg:w-5 lg:h-5 flex-shrink-0">
              {item.icon}
            </div>
            <span className="whitespace-nowrap text-gray-200 lg:text-gray-300">{item.text}</span>
          </Link>
        ))}
      </div>

      {/* Team Section - Only show for authorized roles */}
      {(canAccessTeamMembers || canAccessTeamAvailability || canAccessGPSTracking || canAccessTimeClock) && (
        <CollapsibleNavSection id="team" title="Team">
          {teamItems
            .filter(item => {
              if (item.path === '/dashboard/time-clock' && !canAccessTimeClock) return false;
              if (item.path === '/dashboard/team-members' && !canAccessTeamMembers) return false;
              if (item.path === '/dashboard/team-availability' && !canAccessTeamAvailability) return false;
              if (item.path === '/dashboard/gps-tracking' && !canAccessGPSTracking) return false;
              return true;
            })
            .map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleLinkClick}
                className="block px-4 py-4 lg:px-3 lg:py-2 text-base lg:text-sm rounded-xl lg:rounded-lg text-gray-300 hover:bg-sky-900 active:bg-sky-800 hover:text-white transition-all duration-200 flex items-center gap-4 lg:gap-3 font-medium lg:font-normal border border-transparent hover:border-sky-700 lg:border-0 shadow-sm lg:shadow-none"
              >
                <div className="w-6 h-6 lg:w-5 lg:h-5 flex-shrink-0">
                  {item.icon}
                </div>
                <span className="whitespace-nowrap text-gray-200 lg:text-gray-300">{item.text}</span>
              </Link>
            ))}
        </CollapsibleNavSection>
      )}

      {/* Reporting Section - Only show for authorized roles */}
      {canAccessReports && (
        <CollapsibleNavSection id="reporting" title="Reporting">
          {reportingItems
            .filter(item => (!item.adminOnly || isAdmin) && (!item.roles || item.roles.includes(userRole)))
            .map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleLinkClick}
                className="block px-4 py-4 lg:px-3 lg:py-2 text-base lg:text-sm rounded-xl lg:rounded-lg text-gray-300 hover:bg-sky-900 active:bg-sky-800 hover:text-white transition-all duration-200 flex items-center gap-4 lg:gap-3 font-medium lg:font-normal border border-transparent hover:border-sky-700 lg:border-0 shadow-sm lg:shadow-none"
              >
                <div className="w-6 h-6 lg:w-5 lg:h-5 flex-shrink-0">
                  {item.icon}
                </div>
                <span className="whitespace-nowrap text-gray-200 lg:text-gray-300">{item.text}</span>
              </Link>
            ))}
        </CollapsibleNavSection>
      )}

      {/* Guest App Section - Only show for authorized roles */}
      {canAccessGuestApp && (
        <CollapsibleNavSection id="guest-app" title="Guest App" defaultOpen={false}>
          {guestAppItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleLinkClick}
              className="block px-4 py-4 lg:px-3 lg:py-2 text-base lg:text-sm rounded-xl lg:rounded-lg text-gray-300 hover:bg-sky-900 active:bg-sky-800 hover:text-white transition-all duration-200 flex items-center gap-4 lg:gap-3 font-medium lg:font-normal border border-transparent hover:border-sky-700 lg:border-0 shadow-sm lg:shadow-none"
            >
              <div className="w-6 h-6 lg:w-5 lg:h-5 flex-shrink-0">
                {item.icon}
              </div>
              <span className="whitespace-nowrap text-gray-200 lg:text-gray-300">{item.text}</span>
            </Link>
          ))}
        </CollapsibleNavSection>
      )}

      {/* Help - available to every signed-in role */}
      {Boolean(userRole) && (
        <CollapsibleNavSection
          id="help"
          title="Help"
          className={canAccessSettings ? '' : 'pb-6 lg:pb-4'}
          defaultOpen={false}
        >
          {helpItems.filter((item) => !item.adminOnly || isAdmin).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleLinkClick}
              className="block px-4 py-4 lg:px-3 lg:py-2 text-base lg:text-sm rounded-xl lg:rounded-lg text-gray-300 hover:bg-sky-900 active:bg-sky-800 hover:text-white transition-all duration-200 flex items-center gap-4 lg:gap-3 font-medium lg:font-normal border border-transparent hover:border-sky-700 lg:border-0 shadow-sm lg:shadow-none"
            >
              <div className="w-6 h-6 lg:w-5 lg:h-5 flex-shrink-0">
                {item.icon}
              </div>
              <span className="whitespace-nowrap text-gray-200 lg:text-gray-300">{item.text}</span>
            </Link>
          ))}
        </CollapsibleNavSection>
      )}

      {/* Settings - Only show for authorized roles */}
      {canAccessSettings && (
        <div className="mt-8 lg:mt-6 pt-6 lg:pt-4 border-t border-sky-900 px-4 pb-6 lg:pb-4">
          <Link
            to="/dashboard/settings"
            onClick={handleLinkClick}
            className="block px-4 py-4 lg:px-3 lg:py-2 text-base lg:text-sm rounded-xl lg:rounded-lg text-gray-300 hover:bg-sky-900 active:bg-sky-800 hover:text-white transition-all duration-200 flex items-center gap-4 lg:gap-3 font-medium lg:font-normal border border-transparent hover:border-sky-700 lg:border-0 shadow-sm lg:shadow-none"
          >
            <div className="w-6 h-6 lg:w-5 lg:h-5 flex-shrink-0">
              <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span className="whitespace-nowrap text-gray-200 lg:text-gray-300">Settings</span>
          </Link>
        </div>
      )}
    </nav>
  );
}
